
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Globe, MessageCircle, Send, CheckCircle2 } from 'lucide-react';
import { BUSINESS_INFO } from '../constants';

const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const inputClasses = `
    w-full px-5 py-4 rounded-2xl border border-slate-200 bg-white/50 
    focus:bg-white focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 
    focus:shadow-[0_0_25px_rgba(20,184,166,0.2)] 
    outline-none transition-all duration-500 placeholder:text-slate-400
  `;
  
  const labelClasses = "block text-sm font-bold text-slate-700 mb-2 ml-1 tracking-tight";

  return (
    <div className="min-h-screen py-16 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 reveal-up">
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">Let's Connect</h1>
          <div className="h-2 w-24 bg-teal-600 mx-auto rounded-full mb-8"></div>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg md:text-xl">
            Have questions about our wellness products? Our team is here to provide support and clarity.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
          {/* Business Info Column */}
          <div className="space-y-8 reveal-up" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-3xl font-bold text-slate-900 mb-8">Business Information</h3>
            
            <div className="grid gap-6">
              {[
                { 
                  icon: MapPin, 
                  label: "Office Address", 
                  content: <>{BUSINESS_INFO.address.line1}<br />{BUSINESS_INFO.address.line2}<br />{BUSINESS_INFO.address.city}, {BUSINESS_INFO.address.state} {BUSINESS_INFO.address.zip}</> 
                },
                { 
                  icon: Phone, 
                  label: "Phone & WhatsApp", 
                  content: BUSINESS_INFO.phone,
                  href: `tel:${BUSINESS_INFO.phone}`
                },
                { 
                  icon: Mail, 
                  label: "Email Inquiries", 
                  content: BUSINESS_INFO.email,
                  href: `mailto:${BUSINESS_INFO.email}`
                }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:shadow-2xl hover:border-teal-100 transition-all duration-500 group"
                >
                  <div className="flex items-start space-x-5">
                    <div className="bg-teal-50 p-4 rounded-2xl group-hover:bg-teal-600 group-hover:text-white transition-all duration-500 transform group-hover:rotate-6">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-teal-600 uppercase tracking-widest mb-2">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-slate-900 font-bold text-lg hover:text-teal-600 transition-colors">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-slate-700 font-medium leading-relaxed">{item.content}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <a
                href={`https://wa.me/${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-4 bg-emerald-500 text-white px-8 py-6 rounded-[2rem] font-bold transition-all shadow-2xl shadow-emerald-500/30 hover:bg-emerald-600 hover:scale-[1.02] active:scale-95 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
                <MessageCircle className="h-7 w-7" />
                <span className="text-lg">Chat with Sangeeta Wellness</span>
              </a>
              <div className="mt-8 p-6 bg-slate-900 rounded-3xl text-center border border-slate-800 shadow-xl">
                <p className="text-xs text-slate-500 font-bold uppercase tracking-[0.2em] mb-3">MSME Registration</p>
                <p className="text-lg font-mono text-teal-400 tracking-wider">{BUSINESS_INFO.taxId}</p>
              </div>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="reveal-up" style={{ animationDelay: '0.4s' }}>
            <div className="bg-white p-10 md:p-12 rounded-[3.5rem] shadow-2xl border border-slate-50 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              
              <h3 className="text-3xl font-bold text-slate-900 mb-10 flex items-center">
                <span className="w-10 h-1 h-1 bg-teal-600 mr-4 rounded-full"></span>
                Direct Message
              </h3>

              {submitted ? (
                <div className="py-20 text-center animate-in fade-in zoom-in duration-500">
                  <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-teal-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h4>
                  <p className="text-slate-600">Thank you for reaching out. We'll be in touch soon.</p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-teal-600 font-bold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="space-y-8" onSubmit={handleSubmit}>
                  <div className="group">
                    <label className={labelClasses}>Full Name</label>
                    <input
                      required
                      type="text"
                      className={inputClasses}
                      placeholder="e.g. Rahul Sharma"
                    />
                  </div>
                  
                  <div className="group">
                    <label className={labelClasses}>Email Address</label>
                    <input
                      required
                      type="email"
                      className={inputClasses}
                      placeholder="name@company.com"
                    />
                  </div>

                  <div className="group">
                    <label className={labelClasses}>Inquiry Topic</label>
                    <div className="relative">
                      <select className={`${inputClasses} appearance-none`}>
                        <option>Product Information</option>
                        <option>Lifestyle Support</option>
                        <option>General Support</option>
                        <option>Partnership Inquiry</option>
                      </select>
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <Send className="w-4 h-4 rotate-90" />
                      </div>
                    </div>
                  </div>

                  <div className="group">
                    <label className={labelClasses}>Your Message</label>
                    <textarea
                      required
                      rows={4}
                      className={`${inputClasses} resize-none`}
                      placeholder="How can we help you today?"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-slate-900 text-white py-5 rounded-[1.5rem] font-bold transition-all duration-300 shadow-2xl hover:bg-teal-600 hover:scale-[0.98] active:scale-95 flex items-center justify-center space-x-3 transform group"
                  >
                    <span>Send Inquiry</span>
                    <Send className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>
                  
                  <p className="text-xs text-slate-400 text-center leading-relaxed px-4">
                    By clicking submit, you agree to our <a href="#/privacy-policy" className="text-teal-600 font-bold hover:underline">Privacy Policy</a>. We strictly respect your data.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
